/**
 * Сообщения проверок — ключами словаря, а не текстом (ТЗ §11, фаза 10,
 * задачи 3 и 4).
 *
 * Схемы `packages/contracts` общие для сервера и клиента, и текст их
 * сообщений доезжает до пользователя двумя разными путями: на клиенте —
 * прямо из `safeParse`, с сервера — списком `issues` в теле отказа 400.
 * Если бы текст собирался здесь, язык сообщения о слишком коротком пароле
 * определялся бы языком сервера, а не языком того, кто смотрит на форму.
 *
 * Поэтому наружу уходит ключ и данные для подстановки, а текст собирает
 * клиент. Формат намеренно остаётся обычной строкой:
 *
 *     i18n:validation.password.tooShort:{"min":12}
 *
 * Тип поля `message` в Zod и в теле ошибки менять не пришлось, ключ виден
 * в логах и в ответе как есть, а разобрать его умеет `parseMessageKey`.
 * Строку, которая под формат не подходит, клиент показывает как текст —
 * так ведут себя сообщения сторонних библиотек, и терять их незачем.
 */
import { z } from 'zod';
const PREFIX = 'i18n:';
/**
 * Ключ словаря вместо текста сообщения.
 *
 * Подставляется прямо в Zod: `z.string().min(1, messageKey('...'))`.
 */
export function messageKey(key, params) {
    return params && Object.keys(params).length > 0
        ? `${PREFIX}${key}:${JSON.stringify(params)}`
        : `${PREFIX}${key}`;
}
/** Разбор ключа обратно. `null` — строка не наша, показывать её как текст. */
export function parseMessageKey(message) {
    if (!message.startsWith(PREFIX)) {
        return null;
    }
    const rest = message.slice(PREFIX.length);
    const separator = rest.indexOf(':');
    if (separator === -1) {
        return { key: rest, params: {} };
    }
    const key = rest.slice(0, separator);
    try {
        const parsed = JSON.parse(rest.slice(separator + 1));
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { key, params: {} };
        }
        return { key, params: parsed };
    }
    catch {
        // Данных подстановки нет или они испорчены — ключ всё равно годен,
        // и сообщение без подставленного предела лучше отсутствия сообщения.
        return { key, params: {} };
    }
}
/**
 * Ключ для встроенного отказа Zod.
 *
 * Своих сообщений у схем сотня, а встроенных случаев — «не то число»,
 * «строка короче», «лишние поля» — на порядок больше: они срабатывают
 * везде, где у поля не выписано своё сообщение. Без этой карты каждый
 * такой отказ приезжал бы на экран английской фразой из библиотеки.
 */
function keyForIssue(issue) {
    switch (issue.code) {
        case 'invalid_type':
            return messageKey('validation.zod.invalidType', {
                expected: issue.expected ?? '',
            });
        case 'too_small': {
            const { origin, minimum } = issue;
            const min = Number(minimum ?? 0);
            if (origin === 'string') {
                return messageKey('validation.zod.tooShort', { min });
            }
            if (origin === 'array' || origin === 'set') {
                return messageKey('validation.zod.tooFew', { min });
            }
            return messageKey('validation.zod.tooSmall', { min });
        }
        case 'too_big': {
            const { origin, maximum } = issue;
            const max = Number(maximum ?? 0);
            if (origin === 'string') {
                return messageKey('validation.zod.tooLong', { max });
            }
            if (origin === 'array' || origin === 'set') {
                return messageKey('validation.zod.tooMany', { max });
            }
            return messageKey('validation.zod.tooBig', { max });
        }
        case 'invalid_format':
            return messageKey('validation.zod.invalidFormat', {
                format: issue.format ?? '',
            });
        case 'not_multiple_of':
            return messageKey('validation.zod.notMultipleOf', {
                step: Number(issue.divisor ?? 0),
            });
        case 'unrecognized_keys':
            return messageKey('validation.zod.unrecognizedKeys', {
                keys: (issue.keys ?? []).join(', '),
            });
        case 'invalid_value':
            return messageKey('validation.zod.invalidValue');
        default:
            return messageKey('validation.zod.invalid');
    }
}
/**
 * Включить машинные ключи во всех схемах пакета.
 *
 * Вызывается один раз при загрузке `index.ts`: настройка глобальная, и
 * оставить её на совести каждого приложения значило бы получить сервер
 * с ключами и клиент с английскими фразами библиотеки — или наоборот.
 */
export function installMessageKeys() {
    z.config({
        customError: (issue) => keyForIssue(issue),
    });
}
//# sourceMappingURL=messages.js.map